// Pure torrent-picking logic for "quickplay": title matching, browser-friendliness ranking,
// and finding an episode file inside a season pack. No I/O here - unit tested in pick.test.ts.
import { groupRarVolumes, RAR_VOLUME } from "./rar";

export interface Candidate {
  id: string;
  name: string;
  size: number; // bytes
  seeders: number;
  freeleech?: boolean;
  imdb?: string;
  have?: boolean; // already in our library (no download needed)
}

export interface Want {
  type: "movie" | "tv";
  /** titles to accept (e.g. localized + original title) */
  titles: string[];
  year?: number;
  imdb?: string;
  /** for tv: which episode, and whether we're looking at single-episode releases or season packs */
  season?: number;
  episode?: number;
  pack?: boolean;
  /** download budget in bytes/s (the client's rate limit); 0/undefined = unlimited */
  budget?: number;
  /** running time in minutes (movie, or one episode) */
  runtime?: number;
  /** tv packs: episodes in the season, to estimate per-episode size */
  packEpisodes?: number;
  /** only releases of exactly this resolution (see resolutionOf) */
  height?: number;
  /** what the viewer's device decodes itself (e.g. "hevc" on Apple) - releases needing video transcoding
   *  on this device are strongly avoided */
  caps?: string[];
  /** audio language the viewer chose for this title (2-letter, e.g. "en" for an anime's English dub) */
  audioLang?: string;
  /** the title's original language (TMDB) - a wish for it changes nothing */
  originalLang?: string;
  /** only releases that (by name) carry `audioLang` - "find the dub" */
  dubOnly?: boolean;
}

/** words in release names naming an audio language (dubs / multi-language releases) */
const LANG_WORDS: Record<string, RegExp> = {
  en: / (eng|english|engdub) /,
  de: / (german|ger|deutsch) /,
  fr: / (french|truefrench|vff|vf|vf2|vfi) /,
  es: / (spanish|castellano|latino|esp|spa) /,
  it: / (italian|ita) /,
  pt: / (portuguese|brazilian|por|ptbr|dublado) /,
  ru: / (russian|rus) /,
  pl: / (polish|pol|pldub|dubbing pl) /,
  hi: / (hindi|hin) /,
  ja: / (japanese|jap|jpn) /,
  ko: / (korean|kor) /,
  zh: / (chinese|mandarin|cantonese|chi) /,
  nl: / (dutch|nl) /,
  no: / (norwegian|nor) /,
  sv: / (swedish|swe) /,
  da: / (danish|dan) /,
  fi: / (finnish|fin) /,
};

/**
 * Release fit for a dub wish: the viewer wants audio in `lang` that isn't the title's original language (e.g. the
 * English dub of an anime). Dual-audio / multi releases and releases naming that language get a bonus, so the
 * episode actually HAS the track the player then picks. 0 without such a wish.
 */
export function dubScore(name: string, lang?: string, original?: string): number {
  if (!lang || lang === original) return 0;
  const n = " " + name.toLowerCase().replace(/[^a-z0-9]+/g, " ") + " ";
  if (LANG_WORDS[lang]?.test(n)) return 60;
  if (/ (dual audio|dual|multi audio|multi|multisub dub|dubbed|dub) /.test(n) || /dual[ ._-]?audio/i.test(name)) return 55;
  return 0;
}

/** does this release (probably) carry the wished dub? (no wish = yes) */
export const dubOk = (name: string, lang?: string, original?: string) => !lang || lang === original || dubScore(name, lang, original) > 0;

/** Will this release need its VIDEO transcoded for a device with these decode caps? (audio is cheap) */
export function needsTranscode(name: string, caps: string[] = []): boolean {
  const n = " " + name.toLowerCase().replace(/[^a-z0-9]+/g, " ") + " ";
  const hevc = / (hevc|x265|h265|h 265) /.test(n) || /h\.265/i.test(name);
  const tenBit = / 10bit /.test(n) || /10-?bit|hi10p?/i.test(name);
  if (hevc) return !caps.includes("hevc"); // Apple decodes HEVC incl. 10-bit
  if (tenBit) return true; // 10-bit H.264: no browser/TV decodes it
  if (/ (av1|xvid|divx|vc1|mpeg2) /.test(n)) return true;
  return false;
}

/**
 * "Heavy" release - the "heavy" badge in the search results, and in ranking any non-heavy release is always
 * preferred over every heavy one. Video that usually needs converting (HEVC, 10-bit, AV1, old codecs) or is huge /
 * needs tone-mapping (REMUX, HDR, Dolby Vision). Device-independent, so the badge and the pick agree. Shared with
 * the web UI (web/app.ts).
 */
export function isHeavy(name: string): boolean {
  const n = " " + name.toLowerCase().replace(/[^a-z0-9]+/g, " ") + " ";
  if (/ (hevc|x265|h265|h 265|10bit|10 bit|hi10|hi10p|av1|xvid|divx|vc1|mpeg2|remux|dv|dovi|hdr|hdr10|hdr10plus) /.test(n)) return true;
  return /h\.265|dolby[ ._-]?vision|hdr10\+/i.test(name);
}

/** Resolution a release name advertises: 2160 / 1080 / 720 / 480 (SD, 576p, DVDRip...) or 0 if unknown. */
export function resolutionOf(name: string): number {
  const n = " " + name.toLowerCase().replace(/[^a-z0-9]+/g, " ") + " ";
  if (/ (2160p|4k|uhd) /.test(n)) return 2160;
  if (/ 1080[pi] /.test(n)) return 1080;
  if (/ 720p /.test(n)) return 720;
  if (/ (480p|576p|sd|sdtv|pdtv|dvdrip|xvid|divx) /.test(n)) return 480;
  return 0;
}

/**
 * Bitrate fit: can this release be downloaded faster than it plays? Returns a score adjustment.
 * Uses size / runtime (for packs: size / episodes / runtime). Releases we already have are exempt.
 */
export function bandwidthScore(c: Pick<Candidate, "size" | "have">, want: Want): number {
  if (c.have || !want.budget || !want.runtime || want.runtime <= 0) return 0;
  const bytes = want.pack ? c.size / Math.max(1, want.packEpisodes ?? 10) : c.size;
  const bitrate = bytes / (want.runtime * 60); // bytes/s needed for real-time playback
  const ratio = bitrate / want.budget;
  if (ratio <= 0.7) return 25; // comfortably fits, starts fast and never stalls
  if (ratio <= 0.9) return 15;
  if (ratio <= 1.05) return 0;
  // too big for the line: it will buffer. Strong, growing penalty (but still playable eventually)
  return -Math.min(90, 25 + (ratio - 1) * 60);
}

const GB = 1024 ** 3;

/** lowercase, strip accents, `&` -> and, drop apostrophes, everything else non-alphanumeric -> single space */
export function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** A title as it should be sent to the TorrentLeech search box. */
export function searchTitle(title: string): string {
  // "&" is written as "and" or "&" by different groups - just leave it out of the query
  return normalize(title.replace(/&/g, " "));
}

const pad = (n: number) => String(n).padStart(2, "0");

/** regex that finds SxxEyy / sxxeyy / 1x01 style markers for one episode */
export function episodeRegex(season: number, episode: number): RegExp {
  return new RegExp(
    `(?:^|[^a-z0-9])(?:s0*${season}[ ._-]?e0*${episode}(?!\\d)|0*${season}x0*${episode}(?!\\d))`,
    "i",
  );
}

/** season pack for `season`: "S01" not directly followed by an episode number (also "S01-S03", "Season 1") */
export function seasonPackRegex(season: number): RegExp {
  return new RegExp(`(?:^|[^a-z0-9])(?:s0*${season}(?![ ._-]?e\\d)(?!\\d)|season[ ._-]?0*${season}(?!\\d))`, "i");
}

/** Country / extra tokens that release groups put between a show name and the episode marker. */
const TV_EXTRA = new Set(["us", "uk", "au", "nz", "ca"]);

/**
 * Does the release name start with one of the wanted titles, followed by the year / episode marker /
 * quality tags (so "Dune" doesn't match "Dune Part Two")?
 */
export function titleMatches(name: string, want: Pick<Want, "titles" | "year" | "type">): boolean {
  const n = normalize(name);
  for (const t of want.titles) {
    const nt = normalize(t);
    if (!nt) continue;
    if (n !== nt && !n.startsWith(nt + " ")) continue;
    let rest = n.slice(nt.length).trim().split(" ").filter(Boolean);
    if (want.type === "movie") {
      const next = rest[0];
      if (next && /^(19|20)\d\d$/.test(next)) {
        if (want.year && Math.abs(Number(next) - want.year) > 1) continue;
        return true;
      }
      // no year in the name: accept only if quality tags follow directly
      if (!next || /^(\d{3,4}p|bluray|bdrip|brrip|web|webrip|webdl|hdtv|dvdrip|uhd|remux|repack|proper|complete|x264|h264)$/.test(next)) return true;
      continue;
    }
    // tv: optional year and/or country code, then an S01 / S01E01 / 1x01 / season marker
    while (rest.length && (/^(19|20)\d\d$/.test(rest[0]!) || TV_EXTRA.has(rest[0]!))) rest = rest.slice(1);
    const next = rest[0] ?? "";
    if (/^s\d{1,2}(e\d{1,4})*$/.test(next) || /^\d{1,2}x\d{2,3}$/.test(next) || next === "season" || next === "complete") return true;
  }
  return false;
}

const normImdb = (s?: string) => (s ? s.trim().toLowerCase().replace(/^(tt)?0*/, "tt") : "");

export function imdbMatches(c: Candidate, want: Want): boolean {
  return !!want.imdb && !!c.imdb && normImdb(c.imdb) === normImdb(want.imdb);
}

/** How well will this stream in a browser? Higher = better. Excluded releases get -Infinity. */
export function qualityScore(name: string, size: number, type: Want["type"], pack = false, caps: string[] = []): number {
  const n = " " + name.toLowerCase().replace(/[^a-z0-9]+/g, " ") + " ";
  const has = (re: RegExp) => re.test(n);
  // cam / telesync / telecine / screener: never
  if (has(/ (cam|hdcam|camrip|ts|hdts|telesync|tc|hdtc|telecine|scr|screener|dvdscr) /)) return -Infinity;

  let s = 0;
  const uhd = has(/ (2160p|4k|uhd) /);
  if (has(/ 1080p /)) s += 30;
  else if (has(/ 720p /)) s += 22;
  else if (uhd) s -= 20;
  else if (has(/ (480p|576p|sd|dvdrip|xvid) /)) s += 5;
  else s += 10; // unknown

  // TL names often read "H 264" / "H 265" (dots replaced by spaces)
  if (has(/ (x264|h264|avc|h 264) /) || /h\.264/i.test(name)) s += 25;
  // plays without transcoding the video on this device? that matters more than resolution
  if (needsTranscode(name, caps)) s -= 70;
  // the viewer wants Dolby Atmos and the device passes E-AC-3 through: DD+ Atmos releases (TrueHD Atmos
  // would be converted and lose Atmos, so no bonus for that)
  if (caps.includes("atmos") && caps.includes("eac3") && /atmos/i.test(name) && !/truehd/i.test(name)) s += 25;
  if (has(/ remux /)) s -= 40;
  if (uhd) s -= 15;
  if (has(/ (dv|dovi|hdr|hdr10|hdr10plus) /) || /dolby[ ._-]?vision|hdr10\+/i.test(name)) s -= 25;
  if (has(/ (3d|hsbs|hou) /)) s -= 40;
  // dubbed / foreign-language-only releases, hardcoded subs
  if (has(/ (french|truefrench|vff|german|italian|ita|spanish|castellano|latino|hindi|tamil|telugu|russian|rus|polish|dubbed) /)) s -= 25;
  if (has(/ (hc|hardsub|hardcoded) /)) s -= 10;

  const limit = type === "movie" || pack ? (type === "movie" ? 15 : 40) : 4; // GB
  const gb = size / GB;
  if (gb > limit) s -= 30 + Math.min(40, ((gb - limit) / limit) * 40);
  if (type === "movie" && gb > 0 && gb < 0.6) s -= 10; // probably poor quality
  return s;
}

export interface Scored<C extends Candidate = Candidate> {
  c: C;
  score: number;
  imdb: boolean;
  /** see isHeavy - heavy releases rank after all light ones, whatever their score */
  heavy: boolean;
}

/** best first: light before heavy, then score, then seeders */
export const byPreference = (a: Scored, b: Scored) =>
  Number(a.heavy) - Number(b.heavy) || b.score - a.score || b.c.seeders - a.c.seeders;

/** Filter + rank candidates for what we want; best first. */
export function rank<C extends Candidate>(cands: C[], want: Want): Scored<C>[] {
  const out: Scored<C>[] = [];
  for (const c of cands) {
    if (!(c.seeders > 0) && !c.have) continue;
    if (want.height && resolutionOf(c.name) !== want.height) continue;
    if (want.type === "tv") {
      const season = want.season ?? 1;
      const ep = want.episode ?? 1;
      if (want.pack) {
        if (!seasonPackRegex(season).test(c.name) || /s\d{1,2}[ ._-]?e\d/i.test(c.name)) continue;
      } else if (!episodeRegex(season, ep).test(c.name)) continue;
    }
    if (want.dubOnly && !dubScore(c.name, want.audioLang, want.originalLang)) continue;
    const imdb = imdbMatches(c, want);
    if (!imdb && !titleMatches(c.name, want)) continue;
    const q = qualityScore(c.name, c.size, want.type, !!want.pack, want.caps ?? []);
    if (q === -Infinity) continue;
    let score = q + bandwidthScore(c, want) + dubScore(c.name, want.audioLang, want.originalLang);
    // seeders: availability matters a lot for streaming, but with diminishing returns
    score += Math.min(40, 6 * Math.log2(1 + Math.max(0, c.seeders)));
    if (c.seeders < 3) score -= 15;
    if (c.freeleech) score += 10;
    if (c.have) score += 25;
    if (imdb) score += 1000; // an IMDb id match wins outright
    out.push({ c, score, imdb, heavy: isHeavy(c.name) });
  }
  return out.sort(byPreference);
}

export function pickBest<C extends Candidate>(cands: C[], want: Want): C | undefined {
  return rank(cands, want)[0]?.c;
}

const VIDEO_EXT = /\.(mkv|mp4|m4v|avi|mov|webm|ts|m2ts|wmv|mpg|mpeg)$/i;
const SAMPLE = /(^|[\/._ -])(sample|trailer|extras?|featurettes?)([\/._ -]|$)/i;

/** Find the file of one episode inside a (season pack) torrent. Returns the file index. */
export function findEpisodeFile(
  files: { index: number; path: string; length: number }[],
  season: number,
  episode: number,
): number | undefined {
  const re = episodeRegex(season, episode);
  const vids = files.filter((f) => VIDEO_EXT.test(f.path) && !SAMPLE.test(f.path));
  const hit = vids
    .filter((f) => re.test(f.path.split("/").pop()!) || re.test(f.path))
    .sort((a, b) => b.length - a.length)[0];
  if (hit) return hit.index;
  // a single-episode torrent whose file name lacks the marker
  if (vids.length === 1) return vids[0]!.index;
  // RAR-packed season pack: one volume set per episode (Show.S01E02.rar/.r00/...) - the episode's
  // marker is in the release name, which is the first volume's file name.
  const rarFirstVolumes = groupRarVolumes(files.filter((f) => RAR_VOLUME.test(f.path) && !SAMPLE.test(f.path))).map((g) => g.volumes[0]!);
  const rarHit = rarFirstVolumes
    .filter((f) => re.test(f.path.split("/").pop()!) || re.test(f.path))
    .sort((a, b) => b.length - a.length)[0];
  if (rarHit) return rarHit.index;
  if (rarFirstVolumes.length === 1) return rarFirstVolumes[0]!.index;
  return undefined;
}
